import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faQrcode,
  faClipboardCheck,
  faChevronRight,
  faScrewdriverWrench,
  faImages,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { customerEquipmentService } from '@/services/customer-equipment.service';
import { CUSTOMER_PORTAL_ROUTES, customerJobDetailPath } from '@/routes/customer-portal-routes';
import { CustomerSectionCard } from '@/components/customer-portal/CustomerSectionCard';
import { CustomerStatusBadge } from '@/components/customer-portal/CustomerStatusBadge';
import type { CustomerBadgeTone } from '@/components/customer-portal/CustomerStatusBadge';
import { CustomerKeyValue } from '@/components/customer-portal/CustomerKeyValue';
import { CustomerErrorState } from '@/components/customer-portal/CustomerErrorState';
import { CustomerUnavailableState } from '@/components/customer-portal/CustomerUnavailableState';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';
import { isNotFoundError } from '@/lib/is-not-found-error';
import { isForbiddenError } from '@/lib/is-forbidden-error';
import { CustomerSkeletonDetail } from '@/components/customer-portal/skeletons/CustomerSkeleton';

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  RETIRED: 'Retired',
};

const STATUS_TONE: Record<string, CustomerBadgeTone> = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  RETIRED: 'warning',
};

function fmtDate(v: string | null): string | null {
  if (!v) return null;
  return new Date(v).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function CustomerEquipmentDetailPage() {
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['customer-portal', 'equipment', equipmentId],
    queryFn: () => customerEquipmentService.getById(equipmentId as string),
    enabled: !!equipmentId,
  });

  /** The single way out of every dead end on this route. */
  const backToEquipment = (
    <CustomerButton
      variant="outline"
      icon={faArrowLeft}
      onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.EQUIPMENT)}
    >
      Back to Equipment
    </CustomerButton>
  );

  return (
    <div className="space-y-4 sm:space-y-5">
      <button
        onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.EQUIPMENT)}
        className="customer-pressable -ml-2 inline-flex min-h-11 items-center gap-1.5 rounded-customer-sm px-2 text-sm font-medium text-customer-text-secondary hover:bg-customer-text/[0.05] hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" aria-hidden="true" />
        Back to Equipment
      </button>

      {isLoading && <CustomerSkeletonDetail />}

      {/* Same rule as the job route: a 404 (absent OR outside this contact's
             D-130 location scope) and a 403 are permanent; only a real failure
             offers a retry. */}
      {isError && !isLoading && isNotFoundError(error) && (
        <CustomerUnavailableState
          title="Equipment not available"
          description="This equipment either does not exist or is not available to your account."
          action={backToEquipment}
        />
      )}

      {isError && !isLoading && !isNotFoundError(error) && isForbiddenError(error) && (
        <CustomerUnavailableState
          icon={faLock}
          title="You do not have access to this equipment"
          description="Your account does not have permission to view this equipment. Contact your administrator if you think this is wrong."
          action={backToEquipment}
        />
      )}

      {isError && !isLoading && !isNotFoundError(error) && !isForbiddenError(error) && (
        <CustomerErrorState
          message="We couldn't load this equipment. Please check your connection and try again."
          onRetry={() => void refetch()}
        />
      )}

      {data && !isLoading && !isError && (
        <div className="customer-fade-in space-y-4 sm:space-y-5">
          <CustomerSectionCard>
            <div className="flex items-start gap-3.5">
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-customer-sm bg-customer-primary/10 text-customer-primary-text"
                aria-hidden="true"
              >
                <FontAwesomeIcon icon={faScrewdriverWrench} className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-customer-text-secondary">
                  Equipment
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-semibold leading-tight tracking-tight text-customer-text sm:text-2xl">
                    {data.equipmentName}
                  </h1>
                  <CustomerStatusBadge
                    label={STATUS_LABEL[data.status] ?? data.status}
                    tone={STATUS_TONE[data.status] ?? 'neutral'}
                    icon
                  />
                </div>
                <p className="mt-1 text-xs text-customer-text-secondary">
                  Asset {data.assetNumber} · Serial {data.serialNumber}
                </p>
              </div>
            </div>
          </CustomerSectionCard>

          <div className="grid gap-4 sm:gap-5 lg:grid-cols-2">
            <CustomerSectionCard title="Overview" icon={faScrewdriverWrench}>
              <CustomerKeyValue
                items={[
                  { label: 'Asset Number', value: data.assetNumber },
                  { label: 'Serial Number', value: data.serialNumber },
                  { label: 'Manufacturer', value: data.manufacturerName },
                  { label: 'Equipment Type', value: data.equipmentTypeName },
                  { label: 'Model', value: data.modelName ?? data.modelNumber },
                  { label: 'Installation Date', value: fmtDate(data.installationDate) },
                  { label: 'Purchase Date', value: fmtDate(data.purchaseDate) },
                ]}
              />
              {data.description && (
                <p className="mt-4 border-t border-customer-border pt-3 text-sm leading-5 text-customer-text/80">
                  {data.description}
                </p>
              )}
            </CustomerSectionCard>

            <div className="space-y-4 sm:space-y-5">
              <CustomerSectionCard title="Latest Job" icon={faClipboardCheck}>
                {data.latestInspection ? (
                  <button
                    onClick={() => navigate(customerJobDetailPath(data.latestInspection!.id))}
                    className="customer-pressable -m-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-customer-sm p-2 text-left transition-colors hover:bg-customer-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-customer-primary-text">
                        {data.latestInspection.inspectionNumber}
                      </p>
                      <p className="mt-0.5 text-xs text-customer-text-secondary">
                        {data.latestInspection.status} · Updated{' '}
                        {fmtDate(data.latestInspection.updatedAt)}
                      </p>
                    </div>
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="h-3 w-3 shrink-0 text-customer-text-secondary"
                      aria-hidden="true"
                    />
                  </button>
                ) : (
                  <p className="text-sm text-customer-text-secondary">
                    No jobs recorded for this equipment yet.
                  </p>
                )}
              </CustomerSectionCard>

              <CustomerSectionCard title="QR Code" icon={faQrcode}>
                {data.qrCodeImageUrl ? (
                  <img
                    src={data.qrCodeImageUrl}
                    alt={`QR code for ${data.equipmentName}`}
                    className="h-32 w-32 rounded-customer border border-customer-border bg-white object-contain p-1"
                  />
                ) : (
                  <p className="text-sm text-customer-text-secondary">
                    QR code image not available.
                  </p>
                )}
              </CustomerSectionCard>
            </div>
          </div>

          {data.photoUrls.length > 0 && (
            <CustomerSectionCard title="Photos" icon={faImages}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {data.photoUrls.map((url, index) => (
                  <img
                    key={url}
                    src={url}
                    alt={`${data.modelName ?? data.modelNumber} photo ${index + 1}`}
                    className="aspect-square w-full rounded-customer border border-customer-border object-cover"
                  />
                ))}
              </div>
            </CustomerSectionCard>
          )}
        </div>
      )}
    </div>
  );
}
